import {
  Flex, Text, FileUpload, Button, Box
} from '@huspy/briks-web';
import { BaseSlido } from '@modules/core/components/BaseSlido';
import { useTranslation } from 'react-i18next';
import { MIME_TYPES } from '@mantine/dropzone';
import Loader from '@shared/loader';
import DocumentsIcon from '@icons/Documents';
import { Upload } from '@huspy/briks-icons';
import { BankApplicationDocument } from '@modules/opportunities/entities/bankApplication/bankApplication.entity';
import useUploadBankApplicationDocument from '@modules/opportunities/entities/bankApplication/mutation/useUploadBankApplicationDocument';
import { useState } from 'react';
import { toast } from '@huspy/forge/shared';
import { UploadedDocument } from './UploadedDocument';

const ALLOWED_FILE_TYPES = [
  MIME_TYPES.png,
  MIME_TYPES.jpeg,
  MIME_TYPES.pdf,
  MIME_TYPES.xls,
  MIME_TYPES.xlsx,
  MIME_TYPES.doc,
  MIME_TYPES.docx,
  MIME_TYPES.csv
];
const baseTranslationPath = 'opportunity.caseSubmission.bankForms.bankApplication';

export const UploadSlido = ({
  documents,
  isOpen,
  close,
  hasFormsUploaded,
  opportunityId,
  bankApplicationId,
  bankId,
  clientId,
  setIsUploading,
}: {
  isOpen: boolean;
  close: () => void;
  hasFormsUploaded: boolean;
  documents: BankApplicationDocument[];
  bankApplicationId: string;
  opportunityId: string;
  bankId: string;
  clientId: string | null;
  setIsUploading: (_isUploading:boolean) => void
}) => {
  const { t } = useTranslation();
  const { mutateAsync: uploadBankForm, isPending: isBankFormUploading } = useUploadBankApplicationDocument(opportunityId);
  const [selectedFiles, setSelectedFiles] = useState<File[]>([]);
  /**
   *
   * @param previousFiles
   * @param allFilesFromDropZone
   * @returns Only the new files that are not in the previous state
   * @description DropZone has a bug on it, where each time the user upload a file, it just adds it to the list of files,
   * which means that the same file can be uploaded multiple times.
   * this function filters out the files that are already in the previous state and returns only the new files
   */
  const getNewFilesOnly = (
    previousFiles: File[],
    allFilesFromDropZone: File[]
  ) => {
    // Step 1: Extract file names from the previous state files for quick lookup.
    const oldFileNames = new Set(previousFiles.map((file) => file.name));
    // Step 2: Filter the files from the drop zone, returning only those not in the previous state.
    return allFilesFromDropZone.filter((file) => !oldFileNames.has(file.name));
  };

  const handleOnFilesSelected = async (input: { files: File[] }) => {
    const { files } = input;
    setSelectedFiles(files);
    setIsUploading(true);
    const newFiles = getNewFilesOnly(selectedFiles, files);
    if (newFiles && Array.isArray(newFiles) && newFiles.length > 0) {
      await Promise.all(
        newFiles.map((file) =>
          uploadBankForm({
            bankApplicationId,
            bankId,
            clientId: clientId ?? null,
            file,
          }))
      );
      toast('info', {
        message: t(
          'opportunity.caseSubmission.documentSubmission.notifications.documentUploaded'
        ),
      });
    }
    setIsUploading(false);
  };

  return (
    <BaseSlido
      opened={ isOpen }
      close={ close }
      title={ (
        <Text size='3xl' px='4' py='8'>
          {t(`${baseTranslationPath}.uploadSlido.title`)}
        </Text>
      ) }
      footer={ (
        <Flex justify='end' gap='2'>
          <Button type='button' onClick={ close } data-test='discard-btn'>
            {t(`${baseTranslationPath}.uploadSlido.done`)}
          </Button>
        </Flex>
      ) }
    >
      <Box p='4'>
        <FileUpload.Root
          maxFileSize={ 100_000_000 }
          maxFiles={ 100 }
          onFileAccept={ handleOnFilesSelected }
          accept={ ALLOWED_FILE_TYPES }
          cursor='pointer'
        >
          <FileUpload.Dropzone cursor='pointer' minHeight='140px'>
            <FileUpload.Label cursor='pointer'>
              {isBankFormUploading ? <Loader /> : <Upload />}
            </FileUpload.Label>
            <FileUpload.Trigger cursor='pointer'>
              <Text size='md' fontWeight='medium' data-test='upload-slido-btn'>
                {t(`${baseTranslationPath}.uploadSlido.uploadDescription`)}
              </Text>
            </FileUpload.Trigger>
          </FileUpload.Dropzone>
          <FileUpload.ItemGroup />
          <FileUpload.HiddenInput />
        </FileUpload.Root>
        <Text color='text-tertiary' size='sm'>
          {t(`${baseTranslationPath}.uploadSlido.supportedFormat`)}
        </Text>
        {hasFormsUploaded && (
          <>
            <Flex mt='15' gap='3'>
              <Flex
                backgroundColor='#627595'
                borderRadius='8px'
                height='56px'
                width='56px'
                justify='center'
                align='center'
              >
                <DocumentsIcon />
              </Flex>
              <Flex direction='column' gap='1'>
                <Text size='2xl' weight='medium'>
                  {documents.length}
                  {' '}
                  {t(`${baseTranslationPath}.uploadSlido.fileToUpload`, { count: documents.length })}
                </Text>
                <Text size='sm' color='text-tertiary'>
                  {t(`${baseTranslationPath}.uploadSlido.awesome`)}
                </Text>
              </Flex>
            </Flex>

            <Flex direction='column' gap='spacing-4' mt='26px'>
              {documents.map((document) => (
                <UploadedDocument
                  key={ document.document_external_id }
                  document={ document }
                  bankApplicationId={ bankApplicationId }
                  opportunityId={ opportunityId }
                />
              ))}
            </Flex>
          </>
        )}
      </Box>
    </BaseSlido>
  );
};
